import React, { useRef, useState, useEffect } from 'react';
import { Modal } from '@/components/ui';
import { X, Download } from 'lucide-react';
import { getDeliveryChallanTemplate } from './deliveryChallanTemplate';
import { useOffices } from '@/hooks/useOffices';
import { useCompanyInfo } from '@/pages/settings/hooks/useCompanyInfo';
import { settingsTermService } from '@/pages/settings/services/settingsTermService';

export const DeliveryChallanPreviewModal = ({ isOpen, onClose, sale }) => {
  const { company } = useCompanyInfo();
  const invoiceRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [termsHtml, setTermsHtml] = useState("");
  const { data: stores } = useOffices();

  useEffect(() => {
    if (isOpen) {
      settingsTermService.getTerms()
        .then(res => {
          // Both response.data and res can contain the array depending on interceptor setup
          const data = res?.data || res;
          if (Array.isArray(data)) {
            const activeTerms = data.filter(t => t.status === 'Active' && t.category === 'Sale Invoice');
            if (activeTerms.length > 0) {
              const html = activeTerms.map((t, i) => `
                <div style="margin-bottom: ${i === activeTerms.length - 1 ? '0' : '6px'};">
                  ${t.description ? `<div style="font-weight: 600; margin-bottom: 2px;">${t.description}</div>` : ''}
                  ${t.paymentDescription ? `<div>${t.paymentDescription}</div>` : ''}
                </div>
              `).join('');
              setTermsHtml(html);
            }
          }
        })
        .catch(err => console.error("Failed to load terms:", err));
    }
  }, [isOpen]);

  if (!isOpen || !sale) return null;

  const matchedStore = stores?.find(s => s._id === sale.storeId);
  const enrichedSale = { 
    ...sale, 
    storeDetails: matchedStore 
  };

  const ci = {
    companyName:    company.name    || 'Generic',
    companyAddress: company.address || '',
    companyEmail:   company.email   || '',
    companyPhone:   company.phone   || '',
    companyGst:     company.gst     || '',
    logoUrl:        company.logoUrl || '',
    digitalSignatureUrl: company.digitalSignatureUrl || '',
  };

  const html = getDeliveryChallanTemplate(enrichedSale, ci, termsHtml);

  const handleDownload = async () => {
    if (!invoiceRef.current) return;
    setDownloading(true);
    try {
      const html2pdf = (await import('html2pdf.js/dist/html2pdf.bundle.min.js')).default;
      const filename = `Delivery-Challan-${sale.invoiceNo || sale._id || 'download'}.pdf`;
      const opt = {
        margin:       [4, 4, 4, 4],
        filename,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, allowTaint: true },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(invoiceRef.current).save();
    } catch (error) {
      console.error("Error generating PDF", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-[850px] overflow-hidden print:max-w-full print:m-0 print:border-none print:shadow-none bg-slate-100"
      hideHeader={true}
      bodyClassName="p-0 flex flex-col"
    >
      {/* Action Bar - fixed, does NOT scroll */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200 flex-shrink-0 z-10 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-widest">
          Delivery Challan Preview
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownload}
            disabled={downloading}
            style={{ backgroundColor: '#2b448f', color: '#ffffff' }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg hover:opacity-90 transition-opacity shadow disabled:opacity-60"
          >
            <Download size={16} />
            {downloading ? 'Downloading...' : 'Download PDF'}
          </button>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto flex-1 p-4 sm:p-6 bg-slate-100 custom-scrollbar">
        <div className="w-[715px] mx-auto bg-white shadow-sm ring-1 ring-slate-200 print:shadow-none print:ring-0">
          <div 
            ref={invoiceRef}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>
    </Modal>
  );
};
